import type { AlertProps } from '@magicschool/spellbook/components/Alert';
import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

const statusToIconColor: Record<NonNullable<AlertProps['status']>, IconProps['color']> = {
  primary: 'primary',
  neutral: 'primary',
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
};

export const IconSlot = Slot<IconProps & Pick<AlertProps, 'status'>>({
  displayName: 'Alert.Icon',
  preventOverride: true,
  renderDefault: ({ status, ...props }) => (
    <Icon color={status ? statusToIconColor[status] : 'primary'} data-spellbook-name="alert-icon" {...props} />
  ),
});

const titleVariants = cva('whitespace-nowrap', {
  variants: {
    status: {
      primary: 'text-primary-text-strong',
      neutral: 'text-default-text-strong',
      success: 'text-success-text-strong',
      info: 'text-info-text-strong',
      warning: 'text-warning-text-strong',
      error: 'text-error-text-strong',
    },
  },
});

export const TitleSlot = Slot<TextProps & Pick<AlertProps, 'status'>>({
  displayName: 'Alert.Title',
  preventOverride: true,
  renderDefault: ({ className, status, ...props }) => (
    <Text className={cn(titleVariants({ status }), className)} data-spellbook-name="alert-title" variant="label-1" {...props} />
  ),
});

export const ContentSlot = Slot<TextProps>({
  displayName: 'Alert.Content',
  renderDefault: ({ className, ...props }) => (
    <Text className={cn('text-default-text-soft', className)} data-spellbook-name="alert-content" {...props} />
  ),
});

export const ActionsSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Alert.Actions',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <div className={cn('flex gap-2', className)} data-spellbook-name="alert-actions" {...props} />
  ),
});
